import React, { useState, useEffect } from "react";
import { Button, Grid } from "@material-ui/core";
import { Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import LockIcon from "@material-ui/icons/Lock";
import SecurityOutlined from "@material-ui/icons/SecurityOutlined";

import useStyles from "./loginPageStyles";
import InputPasswordToggle from "./InputPasswordToggle";
import { signin } from "../../actions/auth";
import {
  apiClearStaff2FATrustedDevice,
  apiStaff2FATrustedDeviceStatus,
} from "../../api/index.js";
import { emailFromSignInLink } from "../../utilities/signInLink";
// import lusukuLogo from "assets/img/lusukuLogo517.png";
import lusukuLogo from "assets/img/lusuku_logo_main.png";

const initialState = {
  email: "",
  password: "",
};

const Login = () => {
  const { isLoading } = useSelector((state) => state.auth);

  // A hand-over link ("#email=...", utilities/signInLink) fills the email in,
  // so the password is the only thing the vendor pastes.
  const [linkedEmail] = useState(() =>
    typeof window !== "undefined" ? emailFromSignInLink(window.location.hash) : ""
  );
  const [form, setForm] = useState(() => ({ ...initialState, email: linkedEmail }));
  const dispatch = useDispatch();
  const history = useHistory();

  // Read once, then take the email out of the address bar.
  useEffect(() => {
    if (!linkedEmail) return;
    history.replace({
      pathname: history.location.pathname,
      search: history.location.search,
    });
  }, [linkedEmail, history]);
  const classes = useStyles();

  const [showPassword, setShowPassword] = useState(false);
  const [trustedDeviceActive, setTrustedDeviceActive] = useState(false);
  const [trustStatusLoaded, setTrustStatusLoaded] = useState(false);

  const handleShowPassword = () => setShowPassword(!showPassword);

  useEffect(() => {
    let cancelled = false;
    apiStaff2FATrustedDeviceStatus()
      .then((res) => {
        if (!cancelled) {
          setTrustedDeviceActive(!!res.data?.trustedDevice);
          setTrustStatusLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTrustedDeviceActive(false);
          setTrustStatusLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(signin(form, history));
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleForgetTrustedDevice = async (e) => {
    e.preventDefault();
    try {
      await apiClearStaff2FATrustedDevice();
      setTrustedDeviceActive(false);
      toast.success(
        "This browser will ask for your authenticator code on next sign-in.",
      );
    } catch (_) {
      toast.info(
        "If a trusted device was set, try again or clear site cookies for the API domain.",
      );
    }
  };

  return (
    <div className={classes.pageWrapper}>
      {isLoading ? (
        <Spinner
          animation="grow"
          size="lg"
          variant="danger"
          style={{
            marginTop: "70px",
            top: "50%",
            left: "50%",
            position: "fixed",
          }}
        >
          <span className="sr-only">Loading...</span>
        </Spinner>
      ) : (
        <div className={classes.loginContainer}>
          {/* Left Panel - Branding */}
          <div className={classes.brandingPanel}>
            <div className={classes.brandingContent}>
              <img
                src={lusukuLogo}
                alt="Lusuku Logo"
                className={classes.logo}
              />
              <h1 className={classes.brandTitle}>LUSUKU</h1>
              <p className={classes.brandTagline}>
                Fresh Deliveries, Happy Customers
              </p>
              <div className={classes.featureList}>
                <div className={classes.featureItem}>
                  <span className={classes.featureIcon}>✓</span>
                  <span>Manage orders efficiently</span>
                </div>
                <div className={classes.featureItem}>
                  <span className={classes.featureIcon}>✓</span>
                  <span>Track deliveries in real-time</span>
                </div>
                <div className={classes.featureItem}>
                  <span className={classes.featureIcon}>✓</span>
                  <span>Access analytics & reports</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Login Form */}
          <div className={classes.formPanel}>
            <div className={classes.formContainer}>
              <div className={classes.formHeader}>
                <h2 className={classes.formTitle}>Welcome Back</h2>
                <p className={classes.formSubtitle}>
                  Sign in to your dashboard
                </p>
              </div>

              <form className={classes.form} onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <InputPasswordToggle
                    name="email"
                    label="Email Address"
                    handleChange={handleChange}
                    type="email"
                    value={form.email}
                  />
                  <InputPasswordToggle
                    name="password"
                    label="Password"
                    handleChange={handleChange}
                    type={showPassword ? "text" : "password"}
                    handleShowPassword={handleShowPassword}
                    autoFocus={Boolean(linkedEmail)}
                  />
                </Grid>

                <div className={classes.forgotPassword}>
                  <Link
                    // Carry the typed email over so a vendor does not type it twice.
                    to={{
                      pathname: "/public/resetPassword",
                      state: { email: form.email },
                    }}
                    className={classes.authActionPill}
                  >
                    <LockIcon className={classes.authActionPillIcon} />
                    Forgot password
                  </Link>
                  {trustStatusLoaded && trustedDeviceActive && (
                    <button
                      type="button"
                      onClick={handleForgetTrustedDevice}
                      className={`${classes.authActionPill} ${classes.authActionPillTrust} ${classes.authPillButton}`}
                    >
                      <SecurityOutlined
                        className={classes.authActionPillIcon}
                      />
                      Forget this device (2FA)
                    </button>
                  )}
                </div>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className={classes.submitButton}
                >
                  Sign In
                </Button>
              </form>

              <div className={classes.footer}>
                <p className={classes.footerText}>
                  © {new Date().getFullYear()} Lusuku Fresh Services. All rights
                  reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
